import { AddonBase } from './addon-base';

/**
 * Addon Cart Form Remove Component
 * Handles removing addons from cart with proper state management and error handling
 */
export class AddonCartFormRemove extends AddonBase {
  static CONFIG = {
    SELECTORS: {
      REMOVE_BUTTON: 'button'
    },
    CSS_CLASSES: {
      LOADING: 'addon-remove--loading',
      SUCCESS: 'addon-remove--success',
      ERROR: 'addon-remove--error'
    },
    EVENTS: {
      REMOVE_START: 'addon-cart:remove-start',
      REMOVE_SUCCESS: 'addon-cart:remove-success',
      REMOVE_ERROR: 'addon-cart:remove-error'
    },
    MESSAGES: {
      LOADING: 'Removing...',
      SUCCESS: 'Removed!',
      ERROR: 'Error',
      DEFAULT: 'Remove'
    },
    TIMEOUTS: {
      SUCCESS_FEEDBACK: 1500,
      ERROR_FEEDBACK: 3000
    }
  };

  constructor() {
    super();

    this._handleClick = this._handleClick.bind(this);
  }

  static get observedAttributes() {
    return ['addon-variant-id', 'parent-product-key', 'disabled', 'default-state'];
  }

  /**
   * Get initial state for the component
   * @protected
   * @returns {Object} Initial state object
   */
  _getInitialState() {
    return {
      isLoading: false,
      isAdded: false,
      error: null
    };
  }

  /**
   * Get allowed state keys for default-state parsing
   * @protected
   * @returns {string[]} Array of allowed state keys
   */
  _getAllowedStateKeys() {
    return ['isAdded', 'isLoading', 'error'];
  }

  /**
   * Get state keys to preserve during reinitialization
   * @protected
   * @returns {string[]} Array of state keys to preserve
   */
  _getPreservedStateKeys() {
    return ['isLoading', 'error'];
  }

  /**
   * Validate required attributes
   * @protected
   */
  _validateAttributes() {
    const addonVariantId = this.getAttribute('addon-variant-id');
    const parentProductKey = this.getAttribute('parent-product-key');

    if (!addonVariantId) {
      throw new Error('AddonCartFormRemove: addon-variant-id attribute is required');
    }

    if (!parentProductKey) {
      throw new Error('AddonCartFormRemove: parent-product-key attribute is required');
    }

    this._addonVariantId = addonVariantId;
    this._parentProductKey = parentProductKey;
  }

  /**
   * Cache frequently accessed DOM elements
   * @protected
   */
  _cacheDOMReferences() {
    const { SELECTORS } = AddonCartFormRemove.CONFIG;

    const button = this.querySelector(SELECTORS.REMOVE_BUTTON);
    if (!button) {
      throw new Error('AddonCartFormRemove: Remove button not found');
    }

    this._setDOMRef('button', button);

    // Store original button text
    this._originalButtonText = button.textContent.trim() || AddonCartFormRemove.CONFIG.MESSAGES.DEFAULT;
  }

  /**
   * Setup event listeners with proper cleanup tracking
   * @protected
   */
  _setupEventListeners() {
    const button = this._getDOMRef('button');

    this._addEventListener(button, 'click', this._handleClick);
  }

  /**
   * Setup accessibility attributes
   * @protected
   */
  _setupAccessibility() {
    const button = this._getDOMRef('button');

    if (!button.hasAttribute('aria-label')) {
      button.setAttribute('aria-label', 'Remove addon from cart');
    }

    if (!button.hasAttribute('aria-describedby')) {
      button.setAttribute('aria-describedby', `${this.id || 'addon-remove'}-status`);
    }

    if (this._addonVariantId) {
      button.setAttribute('aria-describedby', `addon-${this._addonVariantId}-description`);
    }

    button.setAttribute('type', 'button');
  }

  /**
   * Handle button click
   * @private
   */
  async _handleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this._state.isLoading || this.hasAttribute('disabled')) {
      return;
    }

    await this._processRemoval();
  }

  /**
   * Handle attribute changes
   * @protected
   * @param {string} name - Attribute name
   * @param {string} newValue - New attribute value
   */
  _handleAttributeChange(name, newValue) {
    switch (name) {
      case 'disabled':
        this._updateDisabledState(newValue !== null);
        break;
      case 'addon-variant-id':
        this._addonVariantId = newValue;
        this._initializeState();
        this._render();
        break;
      case 'parent-product-key':
        this._parentProductKey = newValue;
        this._initializeState();
        this._render();
        break;
      case 'default-state':
        this._reinitializeState();
        break;
    }
  }

  /**
   * Process addon removal from cart
   * @private
   */
  async _processRemoval() {
    if (this._state.isLoading) return;

    this._updateState({ isLoading: true, error: null });
    this._dispatchEvent(AddonCartFormRemove.CONFIG.EVENTS.REMOVE_START);

    try {
      const removalData = this._getRemovalData();
      const cartOperations = await this._addonService.prepareRemoveOperations(removalData);

      await this._executeCartOperations(cartOperations);

      this._updateState({
        isLoading: false,
        isRemoved: true
      });

      this._dispatchEvent(AddonCartFormRemove.CONFIG.EVENTS.REMOVE_SUCCESS, removalData);
      this._showSuccessFeedback();
      this._requestModalClose();

    } catch (error) {
      this._updateState({ isLoading: false });
      this._handleError('Failed to remove addon from cart', error);
    }
  }

  /**
   * Get removal data from attributes
   * @private
   */
  _getRemovalData() {
    if (!this._addonVariantId || !this._parentProductKey) {
      throw new Error('Missing required removal data');
    }

    return {
      addonVariantId: this._addonVariantId,
      parentProductKey: this._parentProductKey
    };
  }

  /**
   * Render component based on current state
   * @private
   */
  _render() {
    this._updateCSSClasses();
    this._updateAccessibility();
  }

  /**
   * Update CSS classes based on state
   * @private
   */
  _updateCSSClasses() {
    const { CSS_CLASSES } = AddonCartFormRemove.CONFIG;

    this.classList.toggle(CSS_CLASSES.LOADING, this._state.isLoading);
    this.classList.toggle(CSS_CLASSES.SUCCESS, this._state.isRemoved);
    this.classList.toggle(CSS_CLASSES.ERROR, !!this._state.error);
  }

  /**
   * Update accessibility attributes
   * @private
   */
  _updateAccessibility() {
    const button = this._domRefs.get('button');
    if (!button) return;

    button.setAttribute('aria-busy', this._state.isLoading.toString());

    if (this._state.error) {
      const addonId = this._addonVariantId || 'addon-remove';
      button.setAttribute('aria-describedby', `${addonId}-error`);
    }
  }

  /**
   * Update disabled state
   * @private
   */
  _updateDisabledState(disabled) {
    const button = this._domRefs.get('button');
    if (!button) return;

    if (disabled || this._state.isLoading) {
      button.disabled = true;
    } else {
      button.disabled = false;
    }
  }

  /**
   * Show success feedback
   * @private
   */
  _showSuccessFeedback() {
    setTimeout(() => {
      if (this._state?.isRemoved) {
        this._updateState({ isRemoved: false });
      }
    }, AddonCartFormRemove.CONFIG.TIMEOUTS.SUCCESS_FEEDBACK);
  }

  /**
   * Request modal closure with success feedback delay
   * @private
   */
  _requestModalClose() {
    // Check if this component is inside a modal
    const modal = this.closest('.addon-modal');

    if (modal) {
      // Find the addon-details component that contains this modal
      const addonDetailsElement = modal.closest('addon-details');

      if (addonDetailsElement && addonDetailsElement.isOpen) {
        // Close the modal with a delay to show success feedback
        setTimeout(() => {
          addonDetailsElement.closeModal();
        }, 500);
      }
    }
  }

  /**
   * Get current state
   */
  get isLoading() {
    return this._state?.isLoading || false;
  }

  get isRemoved() {
    return this._state?.isRemoved || false;
  }

  /**
   * Programmatically trigger addon removal
   */
  async removeAddon() {
    if (!this._state.isLoading) {
      return this._processRemoval();
    }
  }

  /**
   * Reset component to initial state
   */
  reset() {
    this._updateState({
      isLoading: false,
      isRemoved: false,
      error: null
    });
  }
}